/**
 * useAgentChat - 训练广场单智能体对话状态 Hook
 * 
 * 功能：
 * - 管理消息列表（用户 + AI）
 * - SSE 流式接收 AI 回复
 * - 会话管理（新建、切换、加载历史、删除）
 * - 进入时自动加载最近会话
 * - 错误处理与重试
 */

import { useState, useCallback, useRef, useEffect } from 'react';

/**
 * @param {Object} options
 * @param {string} options.expertServiceUrl - ExpertService 基础 URL
 * @param {Object} options.agent - 当前智能体对象 { id, name, ... }
 * @param {string} options.userId - 当前用户 ID
 * @param {string} options.userType - 用户类型：campus/bupt_cas/edunet_cas/guest
 * @param {string} options.trainSessionId - 当前训练会话 ID，用于服务端追溯资源版本
 */
const useAgentChat = ({ expertServiceUrl, agent, userId, userType = 'guest', trainSessionId = '' }) => {
  const [messages, setMessages] = useState([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [sessions, setSessions] = useState([]); // 历史会话列表
  const [isLoadingSessions, setIsLoadingSessions] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(false); // 进入时加载状态
  const [quotaBlocked, setQuotaBlocked] = useState(false); // 额度是否被阻断

  // 用于中止流式请求
  const abortControllerRef = useRef(null);
  // 用于标记组件是否已卸载
  const isMountedRef = useRef(true);
  // 防止重复初始化加载
  const initLoadedAgentRef = useRef(null);
  // 防止异步“自动恢复最近会话”在用户已新建/切换会话后覆盖当前界面。
  const historyRestoreVersionRef = useRef(0);
  const historyRequestVersionRef = useRef(0);
  const historyAbortControllerRef = useRef(null);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      if (historyAbortControllerRef.current) {
        historyAbortControllerRef.current.abort();
      }
    };
  }, []);

  /**
   * 加载历史会话列表
   */
  const loadSessions = useCallback(async () => {
    if (!agent || !userId) return [];
    historyAbortControllerRef.current?.abort();
    const requestVersion = ++historyRequestVersionRef.current;
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 8000);
    historyAbortControllerRef.current = controller;
    setIsLoadingSessions(true);
    try {
      const params = new URLSearchParams({
        user: userId,
        flow: 'plaza',
        command: agent.id,
      });
      const response = await fetch(`${expertServiceUrl}/v1/expert/history?${params}`, { signal: controller.signal });
      if (!response.ok) throw new Error('加载历史会话失败');
      const data = await response.json();
      const sessionList = data.sessions || [];
      if (isMountedRef.current && requestVersion === historyRequestVersionRef.current) {
        setSessions(sessionList);
      }
      return sessionList;
    } catch (err) {
      if (err.name !== 'AbortError') {
        console.error('[AgentChat] 加载历史会话失败:', err);
      }
      if (isMountedRef.current && requestVersion === historyRequestVersionRef.current) {
        setSessions([]);
      }
      return [];
    } finally {
      clearTimeout(timeoutId);
      if (historyAbortControllerRef.current === controller) {
        historyAbortControllerRef.current = null;
      }
      if (isMountedRef.current && requestVersion === historyRequestVersionRef.current) {
        setIsLoadingSessions(false);
      }
    }
  }, [agent, expertServiceUrl, userId]);

  /**
   * 加载指定会话的消息
   */
  const loadSession = useCallback(async (targetSessionId) => {
    if (!targetSessionId) return;
    historyRestoreVersionRef.current += 1;
    try {
      const response = await fetch(`${expertServiceUrl}/v1/expert/history/${targetSessionId}`);
      if (!response.ok) throw new Error('加载会话消息失败');
      const data = await response.json();
      if (isMountedRef.current) {
        setSessionId(targetSessionId);
        const historyMessages = (data.messages || []).map(msg => ({
          role: msg.role,
          content: msg.content,
          timestamp: msg.timestamp || msg.created_at,
          isStreaming: false,
        }));
        setMessages(historyMessages);
        setError(null);
      }
    } catch (err) {
      console.error('[AgentChat] 加载会话消息失败:', err);
      if (isMountedRef.current) {
        setError('加载会话消息失败');
      }
    }
  }, [expertServiceUrl]);

  /**
   * 切换智能体时：自动加载会话列表 → 自动恢复最近会话
   */
  useEffect(() => {
    // React 开发环境会重复执行 effect；同一智能体的第二次执行不能让首个
    // 自动加载请求失效，否则会留下永久的初始加载状态。
    if (agent?.id && agent.id === initLoadedAgentRef.current) return;

    const restoreVersion = ++historyRestoreVersionRef.current;
    // 重置状态
    setMessages([]);
    setSessionId(null);
    setError(null);
    setIsStreaming(false);
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    if (!agent?.id || !userId) {
      setIsInitialLoading(false);
      return;
    }
    initLoadedAgentRef.current = agent.id;

    // 自动加载会话列表并恢复最近会话
    const autoLoad = async () => {
      setIsInitialLoading(true);
      try {
        const params = new URLSearchParams({
          user: userId,
          flow: 'plaza',
          command: agent.id,
        });
        const response = await fetch(`${expertServiceUrl}/v1/expert/history?${params}`);
        if (!response.ok) return;
        const data = await response.json();
        const sessionList = data.sessions || [];

        if (!isMountedRef.current || restoreVersion !== historyRestoreVersionRef.current) return;
        setSessions(sessionList);

        // 如果有历史会话，自动加载最近一条
        if (sessionList.length > 0) {
          const latestSessionId = sessionList[0].session_id;
          const detailResp = await fetch(`${expertServiceUrl}/v1/expert/history/${latestSessionId}`);
          if (!detailResp.ok) return;
          const detailData = await detailResp.json();

          if (!isMountedRef.current || restoreVersion !== historyRestoreVersionRef.current) return;
          setSessionId(latestSessionId);
          const historyMessages = (detailData.messages || []).map(msg => ({
            role: msg.role,
            content: msg.content,
            timestamp: msg.timestamp || msg.created_at,
            isStreaming: false,
          }));
          setMessages(historyMessages);
        }
      } catch (err) {
        console.error('[AgentChat] 自动加载历史失败:', err);
      } finally {
        if (isMountedRef.current && restoreVersion === historyRestoreVersionRef.current) {
          setIsInitialLoading(false);
        }
      }
    };

    autoLoad();
  }, [agent?.id, userId, expertServiceUrl]);

  /**
   * 发送消息并接收 SSE 流式回复
   */
  const sendMessage = useCallback(async (content) => {
    if (!content.trim() || isStreaming || !agent) return;

    setError(null);

    // 添加用户消息
    const userMessage = {
      role: 'user',
      content: content.trim(),
      timestamp: new Date().toISOString(),
    };
    setMessages(prev => [...prev, userMessage]);

    // 添加空的 AI 消息占位（用于流式填充）
    const assistantMessage = {
      role: 'assistant',
      content: '',
      timestamp: new Date().toISOString(),
      isStreaming: true,
    };
    setMessages(prev => [...prev, assistantMessage]);
    setIsStreaming(true);

    // 创建 AbortController
    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      const requestBody = {
        query: content.trim(),
        user: String(userId || 'anonymous'),
        user_type: userType || 'guest',
        unified_user_id: String(userId || 'anonymous'),
        inputs: {
          Flow: 'plaza',
          command: agent.id,
          project_id: trainSessionId || 'plaza',
        },
        response_mode: 'streaming',
      };

      // 如果有会话 ID，传入以复用会话
      if (sessionId) {
        requestBody.conversation_id = sessionId;
      }

      const response = await fetch(`${expertServiceUrl}/v1/expert/chat/stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache',
        },
        body: JSON.stringify(requestBody),
        signal: controller.signal,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || errorData.error || `请求失败 (${response.status})`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder('utf-8');
      let accumulatedContent = '';
      let buffer = '';
    let createdSessionId = '';

      const updateAssistantMessage = (content) => {
        if (!isMountedRef.current) return;
        setMessages(prev => {
          const updated = [...prev];
          const lastIdx = updated.length - 1;
          if (lastIdx >= 0 && updated[lastIdx].role === 'assistant') {
            updated[lastIdx] = { ...updated[lastIdx], content };
          }
          return updated;
        });
      };

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        // 按行解析 SSE 事件
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          const trimmedLine = line.trim();
          if (!trimmedLine || trimmedLine.startsWith(':')) continue;

          if (trimmedLine.startsWith('data: ')) {
            const data = trimmedLine.slice(6);

            if (data === '[DONE]') continue;
            if (data.startsWith('[ERROR]')) {
              setError(data.slice(8));
              continue;
            }
            // 额度阻断：禁用输入，显示错误
            if (data.startsWith('[QUOTA_BLOCKED]')) {
              const msg = data.slice(16).trim() || '您的Token额度已用完，无法继续使用。请联系管理员。';
              if (isMountedRef.current) {
                setQuotaBlocked(true);
                setError(msg);
              }
              continue;
            }
            // 额度预警：显示提示但不阻断
            if (data.startsWith('[QUOTA_WARNING]')) {
              const msg = data.slice(16).trim() || '您的Token额度即将用完，请及时联系管理员。';
              if (isMountedRef.current) {
                setError(msg);
              }
              continue;
            }

            // 尝试解析 JSON（后端首条事件发送 session_id）
            if (data.startsWith('{')) {
              try {
                const parsed = JSON.parse(data);
                if (parsed.session_id) {
                  setSessionId(parsed.session_id);
                  createdSessionId = parsed.session_id;
                  continue;
                }
              } catch (e) {
                // 不是 JSON，当作纯文本处理
              }
            }

            // 后端发送的是纯文本 chunk，换行符被转义为 \\n
            const chunk = data.replace(/\\n/g, '\n');
            accumulatedContent += chunk;
            updateAssistantMessage(accumulatedContent);
          }
        }
      }

      // 流式完成，标记消息为非流式
      if (isMountedRef.current) {
        setMessages(prev => {
          const updated = [...prev];
          const lastIdx = updated.length - 1;
          if (lastIdx >= 0 && updated[lastIdx].role === 'assistant') {
            updated[lastIdx] = {
              ...updated[lastIdx],
              content: accumulatedContent,
              isStreaming: false,
            };
          }
          return updated;
        });
      }
      // 新会话由首条消息触发创建；流结束后刷新左侧列表，确保新记录立即出现。
      if (createdSessionId) {
        const refreshedSessions = await loadSessions();
        // 服务端异步持久化，首轮对话结束时偶尔尚未落库；仅补偿一次，不持续轮询。
        if (!refreshedSessions.some((item) => (item.session_id || item.id) === createdSessionId)) {
          setTimeout(() => {
            if (isMountedRef.current) {
              loadSessions();
            }
          }, 700);
        }
      }
    } catch (err) {
      if (err.name === 'AbortError') {
        console.log('[AgentChat] 请求被中止');
        return;
      }
      console.error('[AgentChat] 发送消息失败:', err);
      if (isMountedRef.current) {
        setError(err.message || '发送消息失败，请重试');
        // 移除空的 assistant 消息
        setMessages(prev => {
          const updated = [...prev];
          const lastIdx = updated.length - 1;
          if (lastIdx >= 0 && updated[lastIdx].role === 'assistant' && !updated[lastIdx].content) {
            updated.pop();
          }
          return updated;
        });
      }
    } finally {
      if (isMountedRef.current) {
        setIsStreaming(false);
      }
      abortControllerRef.current = null;
    }
  }, [agent, expertServiceUrl, isStreaming, sessionId, userId, userType, loadSessions]);

  /**
   * 停止当前流式传输
   */
  const stopStreaming = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    setIsStreaming(false);
    setMessages(prev => {
      const updated = [...prev];
      const lastIdx = updated.length - 1;
      if (lastIdx >= 0 && updated[lastIdx].role === 'assistant') {
        updated[lastIdx] = { ...updated[lastIdx], isStreaming: false };
      }
      return updated;
    });
  }, []);

  /**
   * 新建对话
   */
  const newConversation = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    historyRestoreVersionRef.current += 1;
    setMessages([]);
    setSessionId(null);
    setError(null);
    setIsStreaming(false);
  }, []);

  /**
   * 删除会话
   */
  const deleteSession = useCallback(async (targetSessionId) => {
    if (!targetSessionId || !userId) return false;
    try {
      const params = new URLSearchParams({ user: userId });
      const response = await fetch(
        `${expertServiceUrl}/v1/expert/history/${targetSessionId}?${params}`,
        { method: 'DELETE' }
      );
      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.message || '删除失败');
      }

      if (!isMountedRef.current) return true;

      // 从列表中移除
      setSessions(prev => prev.filter(s => (s.session_id || s.id) !== targetSessionId));

      // 如果删除的是当前会话，切换到最近的其他会话或清空
      if (sessionId === targetSessionId) {
        const remaining = sessions.filter(s => (s.session_id || s.id) !== targetSessionId);
        if (remaining.length > 0) {
          const nextSessionId = remaining[0].session_id || remaining[0].id;
          await loadSession(nextSessionId);
        } else {
          setMessages([]);
          setSessionId(null);
        }
      }

      return true;
    } catch (err) {
      console.error('[AgentChat] 删除会话失败:', err);
      if (isMountedRef.current) {
        setError(err.message || '删除会话失败');
      }
      return false;
    }
  }, [expertServiceUrl, userId, sessionId, sessions, loadSession]);

  /**
   * 清空当前智能体的全部历史会话
   */
  const clearAllSessions = useCallback(async () => {
    if (!agent?.id || !userId) return false;
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    try {
      const params = new URLSearchParams({
        user: userId,
        flow: 'plaza',
        command: agent.id,
      });
      const response = await fetch(`${expertServiceUrl}/v1/expert/history?${params}`);
      if (!response.ok) throw new Error('加载历史会话失败');
      const data = await response.json();
      const sessionList = data.sessions || [];

      await Promise.all(sessionList.map(async (session) => {
        const sid = session.session_id || session.id;
        if (!sid) return;
        const deleteParams = new URLSearchParams({ user: userId });
        const deleteResp = await fetch(
          `${expertServiceUrl}/v1/expert/history/${sid}?${deleteParams}`,
          { method: 'DELETE' }
        );
        if (!deleteResp.ok) {
          const errData = await deleteResp.json().catch(() => ({}));
          throw new Error(errData.message || '删除历史会话失败');
        }
      }));

      if (!isMountedRef.current) return true;
      setMessages([]);
      setSessionId(null);
      setSessions([]);
      setError(null);
      setIsStreaming(false);
      return true;
    } catch (err) {
      console.error('[AgentChat] 清空历史会话失败:', err);
      if (isMountedRef.current) {
        setError(err.message || '清空历史会话失败');
      }
      return false;
    } finally {
      abortControllerRef.current = null;
    }
  }, [agent?.id, expertServiceUrl, userId]);

  /**
   * 清除错误
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * 保存存档到 training_archives
   * @param {Object} opts - { flowName, projectName, trainSessionId, unifiedUserID, userType }
   * @returns {Promise<{ok: boolean, message?: string}>}
   */
  const saveArchive = useCallback(async (opts = {}) => {
    if (!sessionId || !agent || !userId) {
      return { ok: false, message: '无当前会话或智能体信息' };
    }
    try {
      const body = {
        session_id: sessionId,
        user: String(userId),
        flow: 'plaza',
        command: agent.id,
        train_session_id: opts.trainSessionId != null && opts.trainSessionId !== undefined ? String(opts.trainSessionId) : '',
        project_name: opts.projectName || '训练广场',
        flow_name: opts.flowName || '',
        unified_user_id: opts.unifiedUserID != null && opts.unifiedUserID !== undefined ? String(opts.unifiedUserID) : '',
        user_type: opts.userType || 'guest',
        // username 需与 GoMAown session_info.user_id 一致，否则存档列表查不到
        username: opts.username != null && opts.username !== undefined ? String(opts.username) : '',
      };
      const response = await fetch(`${expertServiceUrl}/v1/expert/archive/save`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        return { ok: false, message: data.message || data.error || '保存失败' };
      }
      return { ok: true, message: data.message };
    } catch (err) {
      console.error('[AgentChat] 保存存档失败:', err);
      return { ok: false, message: err.message || '保存存档失败' };
    }
  }, [sessionId, agent, userId, expertServiceUrl]);

  return {
    messages,
    isStreaming,
    error,
    sessionId,
    sessions,
    isLoadingSessions,
    isInitialLoading,
    quotaBlocked,
    sendMessage,
    stopStreaming,
    newConversation,
    loadSessions,
    loadSession,
    deleteSession,
    clearAllSessions,
    clearError,
    saveArchive,
  };
};

export default useAgentChat;
